// src/components/CheckOutInvoice.jsx
import React, { forwardRef, useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Modal } from "antd";
import { FiDownload, FiEye } from "react-icons/fi";
import { StatusTag } from "./ui";
import { fmtDate, fmtMoney } from "./ui/format";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axiosInstance from "../api/axiosInstance";
import { API_BASE_URL } from "../config/apiConfig";
import { useAuth } from "../context/AuthContext";

import "./CheckOutInvoice.css";
import notify from "../utils/notify";

/** Normalize relative -> absolute (for API/Static file URLs) */
const toAbs = (u) => {
  if (!u) return null;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (API_BASE_URL || "").replace(/\/+$/, "");
  return u.startsWith("/") ? `${base}${u}` : `${base}/${u}`;
};

/** Read a Blob into a data URL (PNG/JPEG supported) */
async function blobToDataUrl(blob) {
  return await new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });
}

/** Fetch image via Axios (respects auth/cookies), return data URL */
async function fetchLogoDataUrl(url) {
  if (!url) return null;
  
  try {
    ////console.log("🔄 Fetching logo from:", url);
    
    // Add cache busting and ensure it's a valid URL
    const cacheBusterUrl = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
    
    const resp = await axiosInstance.get(cacheBusterUrl, {
      responseType: 'blob',
      withCredentials: true,
      timeout: 15000,
      headers: {
        'Accept': 'image/png,image/jpeg,image/*,*/*',
        'Cache-Control': 'no-cache'
      }
    });
    
    if (!resp.data || resp.data.size === 0) {
      console.warn("❌ Empty logo blob received");
      return null;
    }
    
    //console.log("✅ Logo blob received, type:", resp.data.type, "size:", resp.data.size);
    
    // Convert to data URL
    const dataUrl = await blobToDataUrl(resp.data);
    return dataUrl;
    
  } catch (error) {
    console.warn("❌ Logo fetch failed, trying fallback:", error);
    return await loadImageDirectly(url);
  }
}

/** Fallback method using canvas */
async function loadImageDirectly(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        
        // White background for transparent images
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        const dataUrl = canvas.toDataURL('image/png');
        //console.log("✅ Fallback logo loaded via canvas");
        resolve(dataUrl);
      } catch (canvasError) {
        console.warn("Canvas conversion failed:", canvasError);
        resolve(null);
      }
    };
    img.onerror = () => {
      console.warn("Direct image loading failed");
      resolve(null);
    };
    img.src = url;
  });
}
/** DEBUG function to test logo data URL */
async function testLogoDataUrl(logoDataUrl) {
  return new Promise((resolve) => {
    if (!logoDataUrl) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => {
      //console.log("✅ Logo test passed - valid image:", img.width, "x", img.height);
      resolve(true);
    };
    img.onerror = () => {
      //console.log("❌ Logo test failed - invalid image data");
      resolve(false);
    };
    img.src = logoDataUrl;
  });
}
/** Optimize PNG for PDF - resize and compress */
async function optimizePngForPdf(dataUrl, maxWidth = 150) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      try {
        // Calculate new dimensions maintaining aspect ratio
        const ratio = Math.min(maxWidth / img.width, 1);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Draw with white background for transparent PNGs
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        resolve(canvas.toDataURL('image/png', 0.9)); // Compressed PNG
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error("Failed to load image for optimization"));
    img.src = dataUrl;
  });
}

/** Convert PNG to JPEG as fallback */
/** Convert PNG to JPEG as fallback */
async function convertPngToJpeg(dataUrl) {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      try {
        canvas.width = img.width;
        canvas.height = img.height;
        
        // White background for JPEG
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        
        resolve(canvas.toDataURL('image/jpeg', 0.9));
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error("Failed to load image for JPEG conversion"));
    img.src = dataUrl;
  });
}


/** Robust getter for logo from hotelDetails prop (various shapes) */
function getHotelLogoUrlFromProps(hotelDetails) {
  const raw =
    hotelDetails?.logoUrl ??
    hotelDetails?.LogoUrl ??
    hotelDetails?.logoURL ??
    hotelDetails?.logo ??
    null;
  return raw ? raw : null;
}

/** Generate PDF and return as Blob for preview */
/** Generate PDF and return as Blob for preview */
async function generatePDFBlob({ logoDataUrl, hotelName, hotelAddress, hotelPhone, invoiceDate, bookingNo, name, roomNumber, checkInDate, checkOutDate, currency, basePrice, vatPct, vatAmtPerNight, levies, computedNights, stayTotal, items, itemsTotal, totalPaid, grandTotal, balanceDue, transactionType }) {
  const doc = new jsPDF("p", "pt", "a4");
  const pw = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  //console.log("🎯 Starting PDF generation...");
  //console.log("📊 Logo data URL present:", !!logoDataUrl);

  // DEBUG: Test the logo data URL before using it
  if (logoDataUrl) {
    const isLogoValid = await testLogoDataUrl(logoDataUrl);
    //console.log("🔍 Logo validity check:", isLogoValid);
    
    if (!isLogoValid) {
      console.warn("⚠️ Invalid logo data URL, skipping logo");
      logoDataUrl = null;
    }
  }

  // Header: logo with enhanced error handling
  let logoAdded = false;
  let logoHeight = 0;
  
  if (logoDataUrl) {
    try {
      //console.log("🖼️ Attempting to add logo to PDF...");
      
      // More robust image loading with timeout
      await new Promise((resolve, reject) => {
        const testImg = new Image();
        const timeout = setTimeout(() => {
          reject(new Error("Logo loading timeout"));
        }, 5000);
        
        testImg.onload = () => {
          clearTimeout(timeout);
          //console.log("✅ Logo loaded successfully, dimensions:", testImg.width, "x", testImg.height);
          resolve();
        };
        testImg.onerror = () => {
          clearTimeout(timeout);
          console.error("❌ Logo failed to load in test");
          reject(new Error("Logo failed to load"));
        };
        testImg.src = logoDataUrl;
      });

      // Get image properties
      let imgProps;
      try {
        imgProps = doc.getImageProperties(logoDataUrl);
        //console.log("📐 Image properties:", imgProps);
      } catch (propsError) {
        console.warn("Could not get image properties:", propsError);
        // Use default dimensions
        imgProps = { width: 100, height: 100 };
      }
      
      // Calculate dimensions to maintain aspect ratio
      const maxWidth = 80;
      const maxHeight = 80;
      const ratio = Math.min(maxWidth / imgProps.width, maxHeight / imgProps.height);
      const width = Math.max(imgProps.width * ratio, 50); // Minimum 50px
      logoHeight = Math.max(imgProps.height * ratio, 50);
      
      //console.log("📏 Logo dimensions for PDF:", width, "x", logoHeight);

      // Try different image formats with better error handling
      const formats = [
        { type: "PNG", method: () => doc.addImage(logoDataUrl, "PNG", margin, y, width, logoHeight) },
        { type: "JPEG", method: async () => {
          const jpegDataUrl = await convertPngToJpeg(logoDataUrl);
          doc.addImage(jpegDataUrl, "JPEG", margin, y, width, logoHeight);
        }},
        { type: "AUTO", method: () => doc.addImage(logoDataUrl, margin, y, width, logoHeight) }
      ];

      for (const format of formats) {
        try {
          if (format.type === "JPEG") {
            await format.method();
          } else {
            format.method();
          }
          logoAdded = true;
          //console.log(`✅ Logo added successfully as ${format.type}`);
          break;
        } catch (formatError) {
          console.warn(`❌ ${format.type} format failed:`, formatError.message);
          continue;
        }
      }

    } catch (imageError) {
      console.warn("❌ All logo embedding attempts failed:", imageError);
      logoAdded = false;
      logoHeight = 0;
    }
  } else {
    console.warn("📝 No logo data URL available for PDF");
  }

  // Hotel info - position based on whether logo was added
  const infoX = logoAdded ? margin + 100 : margin;
  const infoStartY = logoAdded ? y + (logoHeight / 2) - 10 : y; // Center hotel info vertically with logo
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(hotelName, infoX, infoStartY);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(hotelAddress, infoX, infoStartY + 20);
  doc.text(`Contact: ${hotelPhone}`, infoX, infoStartY + 35);

  // Calculate Y position for next section - ensure proper spacing
  if (logoAdded) {
    // Use the bottom of the logo plus some padding
    y = Math.max(y + logoHeight, infoStartY + 45) + 25; // Extra space after logo/hotel info
  } else {
    // No logo, use hotel info bottom plus padding
    y = infoStartY + 45 + 25;
  }

  // ---------- Invoice layout ----------
  const INK = [26, 26, 31];
  const MUTED = [110, 110, 122];
  const LINE = [232, 232, 237];
  const WASH = [250, 250, 251];
  const money = (n) => fmtMoney(currency, n);
  const paid = balanceDue <= 0.005;
  const ph = doc.internal.pageSize.getHeight();

  // Invoice block, top right
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("INVOICE", pw - margin, margin + 16, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`No. ${bookingNo}`, pw - margin, margin + 34, { align: "right" });
  doc.text(fmtDate(invoiceDate), pw - margin, margin + 48, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...(paid ? [31, 138, 76] : [168, 106, 0]));
  doc.text(paid ? "PAID" : "BALANCE DUE", pw - margin, margin + 64, { align: "right" });

  // Divider
  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pw - margin, y);
  y += 22;

  // Billed to / Stay
  const colW = (pw - margin * 2) / 2;
  const label = (txt, x, yy) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(txt.toUpperCase(), x, yy);
  };
  label("Billed to", margin, y);
  label("Stay", margin + colW, y);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...INK);
  doc.text(String(name), margin, y + 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Room ${roomNumber}`, margin, y + 30);

  let sy = y + 16;
  doc.setTextColor(...INK);
  if (checkInDate) {
    doc.text(`Check-in: ${fmtDate(checkInDate)}`, margin + colW, sy);
    sy += 14;
  }
  if (checkOutDate) {
    doc.text(`Check-out: ${fmtDate(checkOutDate)}`, margin + colW, sy);
    sy += 14;
  }
  doc.text(`${computedNights} night${computedNights === 1 ? "" : "s"}`, margin + colW, sy);
  sy += 14;
  if (transactionType) {
    doc.text(`Type: ${transactionType}`, margin + colW, sy);
    sy += 14;
  }
  y = Math.max(y + 30, sy) + 18;

  // Tables: horizontal rules only, numbers right-aligned
  const tableBase = {
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 10,
      cellPadding: { top: 7, bottom: 7, left: 6, right: 6 },
      textColor: INK,
      lineColor: LINE,
      lineWidth: { bottom: 0.6 },
    },
    headStyles: { fillColor: WASH, textColor: MUTED, fontStyle: "bold", fontSize: 8, lineWidth: { bottom: 0.8 } },
    margin: { left: margin, right: margin },
    didParseCell: (d) => {
      if (d.column.index === d.table.columns.length - 1) d.cell.styles.halign = "right";
    },
  };
  const colStyles = {
    0: { cellWidth: "auto", fontStyle: "bold" },
    1: { cellWidth: 170, textColor: MUTED },
    2: { cellWidth: 110 },
  };

  // Stay lines (shown as individual lines; an adjustment line covers discounts so the total always matches)
  const calcStay = (basePrice + vatAmtPerNight + levies) * computedNights;
  const adjustment = stayTotal - calcStay;
  const perNight = (v) => `${computedNights} x ${money(v)}`;
  const stayBody = [["Accommodation", perNight(basePrice), money(basePrice * computedNights)]];
  if (vatPct > 0 || vatAmtPerNight > 0)
    stayBody.push([`VAT (${vatPct}%)`, perNight(vatAmtPerNight), money(vatAmtPerNight * computedNights)]);
  if (levies > 0) stayBody.push(["Levies", perNight(levies), money(levies * computedNights)]);
  if (Math.abs(adjustment) > 0.005)
    stayBody.push([adjustment < 0 ? "Discount / adjustment" : "Adjustment", "", money(adjustment)]);
  stayBody.push([
    { content: "Stay total", colSpan: 2, styles: { fontStyle: "bold" } },
    { content: money(stayTotal), styles: { fontStyle: "bold" } },
  ]);

  doc.autoTable({
    ...tableBase,
    startY: y,
    head: [["Description", "Detail", "Amount"]],
    body: stayBody,
    columnStyles: colStyles,
  });
  let nextY = doc.lastAutoTable.finalY + 18;

  // Extras
  if (items && items.length > 0) {
    doc.autoTable({
      ...tableBase,
      startY: nextY,
      head: [["Extras", "", "Amount"]],
      body: [
        ...items.map((b, index) => [b.description || `Item ${index + 1}`, "", money(b.amount || 0)]),
        [
          { content: "Extras subtotal", colSpan: 2, styles: { fontStyle: "bold" } },
          { content: money(itemsTotal), styles: { fontStyle: "bold" } },
        ],
      ],
      columnStyles: colStyles,
    });
    nextY = doc.lastAutoTable.finalY + 18;
  }

  // Totals (right-hand block)
  doc.autoTable({
    ...tableBase,
    startY: nextY,
    margin: { left: pw - margin - 250, right: margin },
    head: [],
    body: [
      ["Grand total", money(grandTotal)],
      ["Paid", money(totalPaid)],
      [
        { content: "Balance due", styles: { fontStyle: "bold", fontSize: 12, fillColor: paid ? [232, 246, 238] : [255, 244, 220] } },
        { content: money(balanceDue), styles: { fontStyle: "bold", fontSize: 12, fillColor: paid ? [232, 246, 238] : [255, 244, 220] } },
      ],
    ],
    columnStyles: { 0: { cellWidth: "auto", fontStyle: "normal" }, 1: { cellWidth: 120 } },
  });

  const finalY = doc.lastAutoTable.finalY + 28;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text("Thank you for staying with us.", margin, finalY);

  // Footer on every page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`${hotelName}   |   Invoice ${bookingNo}   |   Page ${i} of ${pages}`, pw / 2, ph - 24, { align: "center" });
  }

  return doc.output('blob');
}




const CheckOutInvoice = forwardRef(function CheckOutInvoice(
  { isOpen, onClose, hotelDetails, guestDetails, billingRecords },
  ref
) {
  const { authData } = useAuth();

  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Load hotel logo and pre-process for PDF
 // Load hotel logo and pre-process for PDF
useEffect(() => {
  const fetchAndPrepareLogo = async () => {
    if (!isOpen) return;

    setLogoLoading(true);
    setLogoDataUrl(null);

    try {
      let finalLogoUrl = null;
      const hotelId = authData?.hotelId;

      // Strategy 1: Dedicated logo endpoint
      if (hotelId) {
        try {
          const r = await axiosInstance.get(`/Hotel/${hotelId}/logo`);
          const raw = r?.data?.logoUrl ?? r?.data?.LogoUrl;
          if (raw) {
            // API already returns absolute URL - use it directly
            finalLogoUrl = raw;
            //console.log("✅ Using logo from dedicated endpoint:", finalLogoUrl);
          }
        } catch (error) {
          console.warn("Dedicated logo endpoint failed:", error);
        }
      }

      // Strategy 2: Hotel object endpoint
      if (!finalLogoUrl && hotelId) {
        try {
          const r2 = await axiosInstance.get(`/Hotel/${hotelId}`);
          const raw2 = r2?.data?.logoUrl ?? r2?.data?.LogoUrl ?? r2?.data?.logoURL;
          if (raw2) {
            // Use directly - API should return absolute URL
            finalLogoUrl = raw2;
            //console.log("✅ Using logo from hotel object:", finalLogoUrl);
          }
        } catch (error) {
          console.warn("Hotel object endpoint failed:", error);
        }
      }

      // Strategy 3: From props (apply toAbs only if needed)
      if (!finalLogoUrl) {
        const propLogo = getHotelLogoUrlFromProps(hotelDetails);
        if (propLogo) {
          // Only convert to absolute if it's a relative path
          finalLogoUrl = toAbs(propLogo);
          //console.log("✅ Using logo from props:", finalLogoUrl);
        }
      }

      // Set URL for display
      setLogoUrl(finalLogoUrl);

      // Pre-load and optimize for PDF
      if (finalLogoUrl) {
        //console.log("🔄 Pre-loading logo for PDF:", finalLogoUrl);
        const dataUrl = await fetchLogoDataUrl(finalLogoUrl);
        
        if (dataUrl) {
          try {
            // Test if it's a valid image
            await new Promise((resolve, reject) => {
              const img = new Image();
              img.onload = resolve;
              img.onerror = reject;
              img.src = dataUrl;
            });
            
            // Optimize for PDF
            const optimizedDataUrl = await optimizePngForPdf(dataUrl, 120);
            setLogoDataUrl(optimizedDataUrl);
            //console.log("✅ Logo pre-loaded and optimized for PDF");
          } catch (optimizeError) {
            console.warn("Logo optimization failed, using original:", optimizeError);
            setLogoDataUrl(dataUrl);
          }
        } else {
          console.warn("❌ Failed to fetch logo data URL");
        }
      } else {
        console.warn("❌ No logo URL found from any source");
      }
    } catch (error) {
      console.error("Logo preparation failed:", error);
    } finally {
      setLogoLoading(false);
    }
  };

  fetchAndPrepareLogo();
}, [authData, hotelDetails, isOpen]);

  // Clean up preview URL when component unmounts or closes
  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  // ---------- Values & math ----------
  const currency = guestDetails?.currency || "BWP";
  const bookingNo = guestDetails?.bookingNo || guestDetails?.BookingNo || "N/A";
  const name = guestDetails?.name || "Guest";
  const roomNumber = guestDetails?.roomNumber || "—";

  const checkInDate = useMemo(
    () => (guestDetails?.checkInDate ? new Date(guestDetails.checkInDate) : null),
    [guestDetails?.checkInDate]
  );
  const checkOutDate = useMemo(
    () => (guestDetails?.checkOutDate ? new Date(guestDetails.checkOutDate) : null),
    [guestDetails?.checkOutDate]
  );

  const computedNights = useMemo(() => {
    const apiN = Number(guestDetails?.nights);
    if (apiN && apiN > 0) return apiN;
    if (checkInDate && checkOutDate) {
      const diff = Math.ceil(
        (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
      );
      return Math.max(diff, 1);
    }
    return 1;
  }, [guestDetails?.nights, checkInDate, checkOutDate]);

  const basePrice = Number(guestDetails?.basePrice || 0);
  const vatPct = Number(guestDetails?.vatPercentage || 0);
  const levies = Number(guestDetails?.additionalLevies || 0);

  const vatAmtPerNight = basePrice * (vatPct / 100);
  const stayCalcTotal =
    (basePrice + vatAmtPerNight + levies) * computedNights;

  // prefer API total if present
  const stayTotal =
    guestDetails?.totalAmount != null
      ? Number(guestDetails.totalAmount)
      : stayCalcTotal;

  const items = Array.isArray(billingRecords) ? billingRecords : [];
  const itemsTotal = items.reduce((s, r) => s + Number(r?.amount || 0), 0);

  const depositPaid = Number(guestDetails?.paymentAmount || 0);
  const itemsPaid = items.reduce((s, r) => s + Number(r?.paidAmount || 0), 0);
  const totalPaid = depositPaid + itemsPaid;

  const grandTotal = stayTotal + itemsTotal;
  const balanceDue = Math.max(grandTotal - totalPaid, 0);

  const hotelName =
    hotelDetails?.hotelName || hotelDetails?.name || "Hotel Name";
  const hotelAddress = hotelDetails?.address || "Hotel Address";
  const hotelPhone =
    hotelDetails?.phoneNumber || hotelDetails?.phone || "+000 000 0000";
  const invoiceDate = guestDetails?.invoiceDate
    ? new Date(guestDetails.invoiceDate)
    : new Date();

  // ---------- PDF Functions ----------
  const generatePreviewPDF = async () => {
    if (isGeneratingPreview) return;
    
    setIsGeneratingPreview(true);
    try {
      //console.log("🎯 Generating PDF preview...");
      
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        name,
        roomNumber,
        checkInDate,
        checkOutDate,
        currency,
        basePrice,
        vatPct,
        vatAmtPerNight,
        levies,
        computedNights,
        stayTotal,
        items,
        itemsTotal,
        totalPaid,
        grandTotal,
        balanceDue,
        transactionType: guestDetails?.transactionType,
      });

      // Create object URL for preview
      const previewUrl = URL.createObjectURL(pdfBlob);
      
      // Clean up previous preview URL
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
      
      setPdfPreviewUrl(previewUrl);
      setShowPreview(true);
      
    } catch (error) {
      console.error("❌ PDF preview generation failed:", error);
      notify.error("PDF preview generation failed: " + error.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      //console.log("🎯 Generating PDF for download...");
      
      const pdfBlob = await generatePDFBlob({
        logoDataUrl,
        hotelName,
        hotelAddress,
        hotelPhone,
        invoiceDate,
        bookingNo,
        name,
        roomNumber,
        checkInDate,
        checkOutDate,
        currency,
        basePrice,
        vatPct,
        vatAmtPerNight,
        levies,
        computedNights,
        stayTotal,
        items,
        itemsTotal,
        totalPaid,
        grandTotal,
        balanceDue,
        transactionType: guestDetails?.transactionType,
      });

      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${bookingNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      //console.log("✅ PDF download initiated");
    } catch (error) {
      console.error("❌ PDF download failed:", error);
      notify.error("PDF download failed: " + error.message);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
  };

  if (!isOpen) return null;

  const paid = balanceDue <= 0.005;
  const money = (n) => fmtMoney(currency, n);
  const calcStay = (basePrice + vatAmtPerNight + levies) * computedNights;
  const adjustment = stayTotal - calcStay;
  const nightsTxt = `${computedNights} night${computedNights === 1 ? "" : "s"}`;
  const stayLines = [
    { key: "base", label: "Accommodation", detail: `${computedNights} x ${money(basePrice)}`, amount: basePrice * computedNights },
    vatPct > 0 || vatAmtPerNight > 0
      ? { key: "vat", label: `VAT (${vatPct}%)`, detail: `${computedNights} x ${money(vatAmtPerNight)}`, amount: vatAmtPerNight * computedNights }
      : null,
    levies > 0 ? { key: "lev", label: "Levies", detail: `${computedNights} x ${money(levies)}`, amount: levies * computedNights } : null,
    Math.abs(adjustment) > 0.005
      ? { key: "adj", label: adjustment < 0 ? "Discount / adjustment" : "Adjustment", detail: "", amount: adjustment }
      : null,
  ].filter(Boolean);

  return (
    <>
      <Modal
        open={isOpen}
        onCancel={onClose}
        title={`Invoice ${bookingNo}`}
        width={760}
        centered
        destroyOnHidden
        footer={
          <div className="hms-form-actions" style={{ marginTop: 0 }}>
            <Button onClick={onClose}>Close</Button>
            <Button icon={<FiEye />} onClick={generatePreviewPDF} loading={isGeneratingPreview}>
              Preview PDF
            </Button>
            <Button type="primary" icon={<FiDownload />} onClick={handleDownloadPDF}>
              Download PDF
            </Button>
          </div>
        }
      >
        <div className="inv-doc" ref={ref}>
          <div className="inv-top">
            <div className="inv-brand">
              {logoUrl && !logoLoading ? (
                <img className="inv-logo" src={logoUrl} alt="Hotel logo" />
              ) : (
                <div className="inv-logo is-placeholder">{(hotelName && hotelName[0]) || "H"}</div>
              )}
              <div>
                <div className="inv-hotel">{hotelName}</div>
                <div className="inv-muted">{hotelAddress}</div>
                <div className="inv-muted">Contact: {hotelPhone}</div>
              </div>
            </div>

            <div className="inv-id">
              <div className="inv-title">Invoice</div>
              <div className="inv-muted">No. {bookingNo}</div>
              <div className="inv-muted">{fmtDate(invoiceDate)}</div>
              <StatusTag status={paid ? "available" : "pending"}>{paid ? "Paid" : "Balance due"}</StatusTag>
            </div>
          </div>

          <div className="inv-cols">
            <div>
              <div className="inv-label">Billed to</div>
              <div className="inv-strong">{name}</div>
              <div className="inv-muted">Room {roomNumber}</div>
            </div>
            <div>
              <div className="inv-label">Stay</div>
              {checkInDate && <div>Check-in: {fmtDate(checkInDate)}</div>}
              {checkOutDate && <div>Check-out: {fmtDate(checkOutDate)}</div>}
              <div className="inv-muted">
                {nightsTxt}
                {guestDetails?.transactionType ? ` · ${guestDetails.transactionType}` : ""}
              </div>
            </div>
          </div>

          <table className="inv-lines">
            <thead>
              <tr>
                <th>Description</th>
                <th>Detail</th>
                <th className="r">Amount</th>
              </tr>
            </thead>
            <tbody>
              {stayLines.map((l) => (
                <tr key={l.key}>
                  <td className="inv-strong">{l.label}</td>
                  <td className="inv-muted">{l.detail}</td>
                  <td className="r">{money(l.amount)}</td>
                </tr>
              ))}
              <tr className="is-sub">
                <td colSpan={2}>Stay total</td>
                <td className="r">{money(stayTotal)}</td>
              </tr>
            </tbody>
          </table>

          {items.length > 0 && (
            <table className="inv-lines">
              <thead>
                <tr>
                  <th colSpan={2}>Extras</th>
                  <th className="r">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b, idx) => (
                  <tr key={String(b.billingId ?? b.description ?? idx)}>
                    <td className="inv-strong" colSpan={2}>
                      {b.description || "Item"}
                    </td>
                    <td className="r">{money(b.amount || 0)}</td>
                  </tr>
                ))}
                <tr className="is-sub">
                  <td colSpan={2}>Extras subtotal</td>
                  <td className="r">{money(itemsTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}

          <div className="inv-totals">
            <div className="inv-total-row">
              <span>Grand total</span>
              <span>{money(grandTotal)}</span>
            </div>
            <div className="inv-total-row is-muted">
              <span>Paid</span>
              <span>{money(totalPaid)}</span>
            </div>
            <div className={`inv-total-row is-due ${paid ? "is-paid" : ""}`}>
              <span>Balance due</span>
              <span>{money(balanceDue)}</span>
            </div>
          </div>

          <div className="inv-footer">Thank you for staying with us.</div>
        </div>
      </Modal>

      <Modal
        open={showPreview}
        onCancel={closePreview}
        title={`PDF preview: invoice ${bookingNo}`}
        width={900}
        centered
        destroyOnHidden
        footer={
          <div className="hms-form-actions" style={{ marginTop: 0 }}>
            <Button onClick={closePreview}>Close</Button>
            <Button type="primary" icon={<FiDownload />} onClick={handleDownloadPDF}>
              Download PDF
            </Button>
          </div>
        }
      >
        {pdfPreviewUrl ? (
          <iframe src={pdfPreviewUrl} title="PDF preview" width="100%" style={{ border: 0, height: "70vh" }} />
        ) : (
          <div className="hms-empty">Loading PDF preview...</div>
        )}
      </Modal>
    </>
  );
});

CheckOutInvoice.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hotelDetails: PropTypes.shape({
    hotelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hotelID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    logoUrl: PropTypes.string,
    LogoUrl: PropTypes.string,
    logoURL: PropTypes.string,
    logo: PropTypes.string,
    hotelName: PropTypes.string,
    name: PropTypes.string,
    address: PropTypes.string,
    phoneNumber: PropTypes.string,
    phone: PropTypes.string,
  }),
  guestDetails: PropTypes.shape({
    hotelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    bookingNo: PropTypes.string,
    BookingNo: PropTypes.string,
    name: PropTypes.string,
    roomNumber: PropTypes.string,
    checkInDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    checkOutDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    nights: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    basePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    vatPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    additionalLevies: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoiceDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
  }),
  billingRecords: PropTypes.arrayOf(
    PropTypes.shape({
      billingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      description: PropTypes.string,
      amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      paidAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

export default CheckOutInvoice;